import { css } from '@emotion/react';
import type { BaseEmoji, Data } from 'emoji-mart';
import { observer } from 'mobx-react-lite';
import { lazy, ReactElement, useLayoutEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isWebkit } from 'sdk';

import { useSDK } from '../../../../../app';
import { EMOJI_I18N } from '../../../../../i18n/emoji';
import { styled } from '../../../../../styled';

const NimblePicker = lazy(() => import('emoji-mart/dist-es/components/picker/nimble-picker'));

const webkitWorkaround = css`
	.emoji-mart-scroll {
		width: calc(100% + 40px);
	}

	.emoji-mart-category-label {
		width: calc(100% - 40px);
	}
`;

const blockEmoji = (emoji: BaseEmoji): boolean => {
	const emojiBlockList = [
		'1f9d1-200d-1f9b0',
		'1f9d1-200d-1f9b1',
		'1f9d1-200d-1f9b3',
		'1f9d1-200d-1f9b2',
		'1f9d1-200d-2695-fe0f',
		'1f9d1-200d-2696-fe0f',
		'1f9d1-200d-1f527',
		'1f9d1-200d-1f52c',
		'1f9d1-200d-1f3a8',
		'1f9d1-200d-1f692',
		'1f9d1-200d-1f393',
		'1f9d1-200d-1f33e',
		'1f9d1-200d-1f3ed',
		'1f9d1-200d-1f4bb',
		'1f9d1-200d-2708-fe0f',
		'1f9d1-200d-1f3eb',
		'1f9d1-200d-1f373',
		'1f9d1-200d-1f4bc',
		'1f9d1-200d-1f3a4',
		'1f9d1-200d-1f680',
		'1f9d1-200d-1f9af',
		'1f9d1-200d-1f9bc',
		'1f9d1-200d-1f9bd',
		'263a-fe0f',
	];
	if (emojiBlockList.includes(emoji.unified.toLowerCase())) return false;

	return true;
};

let cachedJson: Data | null = null;
const EMOJI_BOX_HEIGHT = 316;
const TEXTAREA_PADDING_TOP = 8;
export const EmojiPicker = styled(
	observer(
		({
			isShow,
			className,
			onEmojiPick,
		}: {
			isShow: boolean;
			className: string;
			onEmojiPick: (v: BaseEmoji) => void;
		}): ReactElement | null => {
			const [data, setData] = useState<Data | null>(null);
			const { chatAssetsPath, uiStateStore } = useSDK();
			const emojiPath = useMemo(() => chatAssetsPath + '/emoji-list.json', [chatAssetsPath]);
			useLayoutEffect(() => {
				if (!cachedJson) {
					fetch(emojiPath)
						.then((resp) => resp.json())
						.then((json) => {
							setData(json);
							cachedJson = json;
						});
				} else {
					setData(cachedJson);
				}
			}, [emojiPath]);
			const intl = useIntl();
			if (!data || !isShow) return null;
			return (
				<div className={className} onMouseDown={(e): void => e.stopPropagation()}>
					<div>
						<NimblePicker
							data={data}
							onClick={onEmojiPick}
							i18n={EMOJI_I18N(intl)}
							perLine={Math.floor((uiStateStore.chatSize?.width || 280) / 45)}
							showPreview={false}
							useButton={false}
							native
							emojisToShowFilter={blockEmoji}
							showSkinTones={false}
							exclude={['flags']}
						/>
					</div>
				</div>
			);
		}
	)
)(
	({ position = 'fixed', wrapperTop = 0 }: { position?: 'absolute' | 'fixed'; wrapperTop?: number }) => ({
		'& > div': {
			position,
			bottom: position === 'absolute' && 0,
			top:
				position === 'absolute'
					? 'initial'
					: wrapperTop && wrapperTop - EMOJI_BOX_HEIGHT - TEXTAREA_PADDING_TOP,
		},
	}),
	css`
		display: flex;
		justify-content: flex-end;
		position: relative;
		z-index: 100050;

		& .emoji-mart {
			user-select: none;
			.emoji-mart-anchor-icon {
				display: flex;
				justify-content: center;
			}
		}

		.emoji-mart-search {
			display: none;
		}

		.emoji-mart-emoji > span {
			font-size: 22px;
		}

		& * {
			box-sizing: border-box;
			scrollbar-width: none;
		}
	`,
	isWebkit && webkitWorkaround
);
